<script>
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';
  import { Spinner } from '@client/components/spinner';

  import { RoutingService } from '@client/services/routing';
  import { SignaturesService } from '@client/services/signatures';
  import { TagBlocksService } from '@client/services/tag-blocks';

  import { userStore } from '@client/stores/user';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getUserSignature = async (signature) => {
    if (signature.html) {
      return signature.html;
    }

    const data = {
      username: 'adminuser',
      scratchblockname: signature.rulename,
      testData: {
        testsender: 'adminuser'
      }
    };

    try {
      const { rendered } = await SignaturesService.getSignature(data);
      signature.html = rendered;
      return rendered;
    } catch (error) {
      console.error('Failed to load signature preview:', error);
      return '';
    }
  };

  const getSignatures = async () => {
    const signatures = await TagBlocksService.getAll();
    return signatures
      .filter(({ archived }) => !archived)
      .map((signature) => ({
        label: signature.name || signature.rulename,
        value: signature.rulename,
        ...signature
      }));
  };

  const onDeleteSignature = async (signature) => {
    // Check if signature is used in routing
    const routes = await RoutingService.getAll();
    const usedInRoutes = routes.filter((route) => route.blockname === signature.rulename);

    const response = await TagBlocksService.delete(signature);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.DELETE_SIGNATURE', { values: { rulename: signature.rulename } }), 'error');
      throw new Error($format('label.FAILURE.DELETE_SIGNATURE'));
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: signature.rulename } }));

    step.data = {
      id: signature.rulename,
      rollbackData: {
        signature: { ...signature },
        deletedRoutes: usedInRoutes
      }
    };
  };

  const rollbackDelete = async () => {
    if (!step.data?.rollbackData) {
      throw new Error($format('label.NO_ROLLBACK_DATA'));
    }

    const { signature } = step.data.rollbackData;

    // Prepare signature data for recreation
    const signatureData = {
      rulename: signature.rulename,
      htmltemplate: signature.htmltemplate,
      texttemplate: signature.texttemplate,
      replyhtmltemplate: signature.replyhtmltemplate,
      replytexttemplate: signature.replytexttemplate,
      replytemplateenabled: signature.replytemplateenabled,
      params: signature.params,
      name: signature.name,
      archived: signature.archived || false
    };

    // Restore the deleted signature
    const response = await TagBlocksService.create(signatureData);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ROLLBACK_SIGNATURE_DELETE'), 'error');
      throw new Error($format('label.FAILURE.ROLLBACK_SIGNATURE_DELETE'));
    }

    notify($format('label.SUCCESS.ROLLBACK_SIGNATURE_DELETE'));

    return { success: true };
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  getFunction={getSignatures}
  deleteFunction={onDeleteSignature}
  rollbackFunction={rollbackDelete}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      <div class="signature-preview">
        {#if !item.html}
          {#await getUserSignature(item)}
            <div class="loading">
              <Spinner show inline />
            </div>
          {:then html}
            {#if html}
              <iframe
                tabindex="-1"
                style="width: 100%; border: none;"
                scrolling="no"
                sandbox="allow-same-origin"
                title={item.name || item.rulename}
                srcdoc="<html><body><div style='transform: scale(.6,.6); transform-origin: left top; -moz-transform: scale(.6,.6); -moz-transform-origin: left top;'>{html ||
                  ''}</div></body></html>"
              />
            {:else}
              <div class="no-preview">
                <p>No preview available</p>
              </div>
            {/if}
          {:catch error}
            <div class="error-preview">
              <p>Failed to load preview</p>
            </div>
          {/await}
        {:else}
          <iframe
            tabindex="-1"
            style="width: 100%; border: none;"
            scrolling="no"
            sandbox="allow-same-origin"
            title={item.name || item.rulename}
            srcdoc="<html><body><div style='transform: scale(.6,.6); transform-origin: left top; -moz-transform: scale(.6,.6); -moz-transform-origin: left top;'>{item.html ||
              ''}</div></body></html>"
          />
        {/if}
      </div>
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .signature-preview {
    background-color: var(--color-white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-base);
    min-height: 100px;
    padding: var(--padding-small);

    iframe {
      min-height: 100px;
    }

    .loading {
      align-items: center;
      display: flex;
      justify-content: center;
      min-height: 100px;
      padding: var(--padding-large);
    }

    .no-preview,
    .error-preview {
      align-items: center;
      color: var(--color-gray-600);
      display: flex;
      justify-content: center;
      min-height: 100px;
      padding: var(--padding-base);

      p {
        margin: 0;
      }
    }

    .error-preview {
      color: var(--color-red-600);
    }
  }
</style>
